import { useContext, useEffect, useReducer } from 'react'
import {
    Typography,
    TextField,
    Button,
    IconButton,
    Tooltip,
    Card,
    CardMedia,
    AccordionDetails,
    AccordionSummary,
    Accordion,
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Chip,
    Stack,
    Fade,
    Modal,
} from '@mui/material'
import {
    FormSection,
    SaveButton,
    UniversalModal,
    Grid,
    Autocomplete as UniversalAutoComplete,
    PDFViewer,
} from '../../UniversalComponents'
import {
    DriveFolderUpload,
    ExpandMore,
    PictureAsPdf,
    VideoLibrary,
    Photo,
    RestartAlt,
    Check,
    HelpOutline,
    FiberManualRecord,
    UploadFile,
    Close,
} from '@mui/icons-material'

import { getManufacturers, addManufacturer } from '../../../apiRoutes'
import { MuiColorInput } from 'mui-color-input'
import { alpha, styled } from '@mui/material/styles'
import responseError from '../../../context/responseError/responseError'
import { parseFormWithIndentifier } from '../../../utilities/functionTools'
import { ToastMessage } from '../../../context/ToastPopUpContext'
import { Authentication } from '../../../context/AuthenticationContext'
import ManufacturerDTCUploader from './manufacturerDTCForm'
import ManufacturerAdmins from './ManufacturerAdmins'
import ManufacturerNotificationAdmins from './ManufacturerNotificationAdmins'

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
})

const ManufacturerForm = ({
    row,
    formDescription,
    onClose,
    permissionCheck,
}) => {
    const defaultState = {
        formData: {
            manufacturerName: row.manufacturerName || '',
            manufacturerContactName: row.manufacturerContactName || '',
            manufacturerContactEmail: row.manufacturerContactEmail || '',
            manufacturerType: row.manufacturerType || '',
            manufacturerLogo: row.manufacturerLogo || '',
            manufacturerVideo: row.manufacturerVideo || '',
            manufacturerEula: row.manufacturerEula || '',
            manufacturerPrivacyPolicy: row.manufacturerPrivacyPolicy || '',
            manufacturerPrimaryColor: row.manufacturerPrimaryColor || '',
            manufacturerSecondaryColor: row.manufacturerSecondaryColor || '',
            manufacturerTertiaryColor: row.manufacturerTertiaryColor || '',
            manufacturerAdmins: row.manufacturerAdmins || [],
            manufacturerNotificationAdmins:
                row.manufacturerNotificationAdmins || [],
            id: row.id || '',
            dtcData: '',
        },
        formValidation: {
            manufacturerNameValidation: false,
            manufacturerContactNameValidation: false,
            manufacturerContactEmailValidation: false,
            manufacturerTypeValidation: false,
            isFormDirty: false,
            isFormValid: false,
        },

        permissionsCheck: permissionCheck,
        logo: '',
        logoFile: {},
        loginVideo: '',
        loginVideoFile: {},
        eula: '',
        eulaFile: {},
        privacyPolicy: '',
        privacyFile: {},
        openEula: false,
        openPrivacyPolicy: false,
        openHelper: false,
        openGuidelines: false,
        openLogoPreview: false,
        expandPrivacyPolicy: false,
        expandEula: false,
    }

    const reducer = (state, newState) => ({ ...state, ...newState })

    const [state, setState] = useReducer(reducer, defaultState)
    const setToastMessage = useContext(ToastMessage)
    const currentUser = useContext(Authentication)
    const { userProfile, user } = currentUser.auth
    const { superAdmin, userProfilePermissions } = userProfile

    const {
        formData,
        formValidation,
        permissionsCheck,
        logo,
        logoFile,
        loginVideo,
        loginVideoFile,
        eula,
        privacyPolicy,
        privacyFile,
        eulaFile,
        openEula,
        openPrivacyPolicy,
        openHelper,
        openGuidelines,
        openLogoPreview,
        expandPrivacyPolicy,
        expandEula,
    } = state
    const {
        manufacturerName,
        manufacturerContactEmail,
        manufacturerContactName,
        manufacturerType,
        manufacturerLogo,
        manufacturerVideo,
        manufacturerEula,
        manufacturerPrivacyPolicy,
        manufacturerPrimaryColor,
        manufacturerSecondaryColor,
        manufacturerTertiaryColor,
        manufacturerAdmins,
        manufacturerNotificationAdmins,
        id,
        dtcData,
    } = formData

    const {
        manufacturerContactNameValidation,
        manufacturerContactEmailValidation,
        manufacturerNameValidation,
        manufacturerTypeValidation,
        isFormDirty,
        isFormValid,
    } = formValidation

    const handleChange = (e: any) => {
        const { name, value } = e.target

        setState({
            formData: { ...formData, [name]: value },
        })
    }

    const handleAdminsChange = (newAdmins: any[]) => {
        const validIds = new Set(
            (newAdmins || []).map((a: any) =>
                typeof a === 'string' ? a : a?._id
            )
        )

        const filteredNotificationAdmins = (
            manufacturerNotificationAdmins || []
        ).filter((u: any) => validIds.has(u._id))

        setState({
            formData: {
                ...formData,
                manufacturerAdmins: newAdmins,
                manufacturerNotificationAdmins: filteredNotificationAdmins,
            },
            formValidation: {
                ...formValidation,
                isFormDirty: true,
            },
        })
    }

    const handleNotificationAdminsChange = (newNotificationAdmins: any[]) => {
        setState({
            formData: {
                ...formData,
                manufacturerNotificationAdmins: newNotificationAdmins,
            },
            formValidation: {
                ...formValidation,
                isFormDirty: true,
            },
        })
    }

    const handleSubmit = async () => {
        try {
            const adminIds = (formData.manufacturerAdmins || []).map((a: any) =>
                typeof a === 'string' ? a : a?._id
            )

            const notificationAdminIds = (
                formData.manufacturerNotificationAdmins || []
            ).map((a: any) => (typeof a === 'string' ? a : a?._id))

            const newFormData = {
                ...formData,
                manufacturerAdmins: JSON.stringify(adminIds),
                manufacturerNotificationAdmins:
                    JSON.stringify(notificationAdminIds),
                updatedBy: user,
            }

            const selectedFiles = [
                logoFile,
                loginVideoFile,
                eulaFile,
                privacyFile,
            ]

            const parsedFormData = parseFormWithIndentifier(
                newFormData,
                selectedFiles
            )
            let res: any

            {
                res = await addManufacturer(parsedFormData)
                if (res) {
                    const { data, status } = res

                    if (data && status === 200) {
                        setToastMessage(
                            `Manufactuer: ${
                                data?.manufacturer?.manufacturerName
                            } was ${row.id ? 'edited' : 'added'} successfully`,
                            'success'
                        )

                        onClose(row.manufacturerName, data?.manufacturer)
                    } else {
                        responseError(res, row)
                    }
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const getEngineManufacturerData = async () => {
        try {
            const engineOEM = await getManufacturers(true)

            if (engineOEM && engineOEM.length !== 0) {
                setState({ engineOEM })
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    useEffect(() => {
        getEngineManufacturerData()
    }, [])

    const validateOnBlur = (e: any) => {
        const { name, value } = e.target

        if (value !== '') {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: false,
                    isFormDirty: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: true,
                    isFormDirty: true,
                },
            })
        }
    }

    useEffect(() => {
        const emailValid = () => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailRegex.test(manufacturerContactEmail)
        }
        if (
            isFormDirty &&
            !manufacturerContactNameValidation &&
            !manufacturerContactEmailValidation &&
            !manufacturerNameValidation &&
            !manufacturerTypeValidation &&
            emailValid() &&
            manufacturerContactName &&
            manufacturerContactEmail &&
            manufacturerType &&
            manufacturerName
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [
        isFormDirty,
        manufacturerNameValidation,
        manufacturerContactNameValidation,
        manufacturerContactEmailValidation,
        manufacturerTypeValidation,
        manufacturerContactEmail,
        manufacturerContactName,
        manufacturerType,
    ])

    const emailCheck = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(manufacturerContactEmail)
    }

    const manufacturerOptions = ['boat', 'engine']

    useEffect(() => {
        if (row) {
            setState({
                formData: {
                    ...formData,
                    manufacturerName: row.manufacturerName || '',
                    manufacturerContactName: row.manufacturerContactName || '',
                    manufacturerContactEmail:
                        row.manufacturerContactEmail || '',
                    manufacturerType: row.manufacturerType || '',
                    manufacturerAdmins: row.manufacturerAdmins || [],
                    manufacturerNotificationAdmins:
                        row.manufacturerNotificationAdmins || [],
                    id: row.id || '',
                },
            })
        }
    }, [row])

    const handleFileSelect = (event, isImage = false, isVideo = false) => {
        if (event.target.files && event.target.files.length !== 0) {
            const file = event.target.files[0]

            const reader = (file, callback) => {
                const fr = new FileReader()
                fr.onload = () => callback(null, fr.result)
                fr.onerror = (err) => callback(err)
                fr.readAsDataURL(file)
            }

            if (isImage) {
                reader(file, (err, res) => {
                    setState({
                        logo: res,
                        logoFile: file,
                        formValidation: {
                            ...formValidation,
                            isFormDirty: true,
                        },
                    })
                })
            } else if (isVideo) {
                reader(file, (err, res) => {
                    setState({
                        loginVideo: res,
                        loginVideoFile: file,
                        formValidation: {
                            ...formValidation,
                            isFormDirty: true,
                        },
                    })
                })
            } else {
                setState({
                    formValidation: { ...formValidation, isFormDirty: true },
                })
            }
        }
    }

    const handleEulaUpload = (event: any) => {
        if (event.target.files && event.target.files.length !== 0) {
            const file = event.target.files[0]
            ;(file as any).tag = 'eula'

            const reader = (file, callback) => {
                const fr = new FileReader()
                fr.onload = () => callback(null, fr.result)
                fr.onerror = (err) => callback(err)
                fr.readAsDataURL(file)
            }
            reader(file, (err, res) => {
                setState({
                    eula: res,
                    eulaFile: file,
                    expandEula: true,
                    formValidation: {
                        ...formValidation,
                        isFormDirty: true,
                    },
                })
            })
        }
    }

    const handlePrivacyUpload = (event: any) => {
        if (event.target.files && event.target.files.length !== 0) {
            const file = event.target.files[0]
            ;(file as any).tag = 'privacy'

            const reader = (file, callback) => {
                const fr = new FileReader()
                fr.onload = () => callback(null, fr.result)
                fr.onerror = (err) => callback(err)
                fr.readAsDataURL(file)
            }

            reader(file, (err, res) => {
                setState({
                    privacyPolicy: res,
                    privacyFile: file,
                    expandPrivacyPolicy: true,
                    formValidation: {
                        ...formValidation,
                        isFormDirty: true,
                    },
                })
            })
        }
    }

    const handleColorChange = (name, value) => {
        setState({
            formData: {
                ...formData,
                [name]: value,
            },
            formValidation: {
                ...formValidation,
                isFormDirty: true,
            },
        })
    }

    const handleReset = () =>
        setState({
            logoFile: defaultState.logoFile,
            logo: defaultState.logo,
            loginVideo: defaultState.loginVideo,
            loginVideoFile: defaultState.loginVideoFile,
            eula: defaultState.eula,
            privacyPolicy: defaultState.privacyPolicy,
            eulaFile: defaultState.eulaFile,
            privacyFile: defaultState.privacyFile,
            formData: defaultState.formData,
            formValidation: defaultState.formValidation,
        })

    const handleLogoReset = () =>
        setState({
            ...state,
            formData: {
                ...formData,
                manufacturerLogo: formData.manufacturerLogo,
            },
            logoFile: defaultState.logoFile,
            logo: defaultState.logo,
            formValidation: defaultState.formValidation,
        })

    const handleVideoReset = () =>
        setState({
            ...state,
            formData: {
                ...formData,
                manufacturerVideo: formData.manufacturerVideo,
            },
            loginVideo: defaultState.loginVideo,
            loginVideoFile: defaultState.loginVideoFile,
            formValidation: { ...formValidation },
        })

    const handleDocumentReset = () =>
        setState({
            ...state,
            formData: {
                ...formData,
                manufacturerPrivacyPolicy:
                    defaultState.formData.manufacturerPrivacyPolicy,
                manufacturerEula: defaultState.formData.manufacturerEula,
            },
            eula: defaultState.eula,
            privacyPolicy: defaultState.privacyPolicy,
            eulaFile: defaultState.eulaFile,
            privacyFile: defaultState.privacyFile,
            formValidation: defaultState.formValidation,
        })

    const handleColorReset = () =>
        setState({
            formData: {
                ...formData,
                manufacturerPrimaryColor:
                    defaultState.formData.manufacturerPrimaryColor,
                manufacturerSecondaryColor:
                    defaultState.formData.manufacturerSecondaryColor,
                manufacturerTertiaryColor:
                    defaultState.formData.manufacturerTertiaryColor,
            },
            formValidation: defaultState.formValidation,
        })

    return (
        <Grid container spacing={2}>
            <Grid
                item
                xs={12}
                sx={{ padding: 1, marginTop: -2.5, marginBottom: -3 }}
            >
                <Typography align="center" variant="h5" color={'primary'}>
                    {formDescription}
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <FormSection
                    title="Manufacturer Details"
                    reset={
                        <Button onClick={() => setState({ openHelper: true })}>
                            <HelpOutline />
                        </Button>
                    }
                >
                    <Grid item xs={12} md={4}>
                        <TextField
                            required={true}
                            label="Manufacturer Name"
                            name="manufacturerName"
                            onChange={handleChange}
                            fullWidth
                            onBlur={validateOnBlur}
                            value={manufacturerName}
                            error={
                                manufacturerNameValidation && !manufacturerName
                            }
                            helperText={
                                manufacturerNameValidation && !manufacturerName
                                    ? 'Please enter a Enter A Manufacturer Name.'
                                    : ''
                            }
                            disabled={permissionsCheck}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required={true}
                            label="Manufacturer Contact Name"
                            name="manufacturerContactName"
                            onChange={handleChange}
                            fullWidth
                            onBlur={validateOnBlur}
                            value={manufacturerContactName}
                            error={
                                manufacturerContactNameValidation &&
                                !manufacturerContactName
                            }
                            helperText={
                                manufacturerContactNameValidation &&
                                !manufacturerContactName
                                    ? 'Please enter a Manufacturer Contact Name.'
                                    : ''
                            }
                            disabled={permissionsCheck}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            required={true}
                            label="Manufacturer Contact Email"
                            name="manufacturerContactEmail"
                            onChange={handleChange}
                            fullWidth
                            onBlur={validateOnBlur}
                            value={manufacturerContactEmail}
                            error={
                                manufacturerContactEmailValidation === false &&
                                manufacturerContactEmail
                                    ? !emailCheck()
                                    : manufacturerContactEmailValidation &&
                                      !manufacturerContactEmail
                            }
                            helperText={
                                manufacturerContactEmailValidation === false &&
                                !manufacturerContactEmail
                                    ? manufacturerContactEmailValidation &&
                                      !manufacturerContactEmail
                                    : !emailCheck()
                                    ? 'Please enter a Manufacturer Contact Email.'
                                    : ''
                            }
                            disabled={permissionsCheck}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <UniversalAutoComplete
                            options={manufacturerOptions}
                            required={true}
                            label="Manufacturer Type"
                            name="manufacturerType"
                            handleChange={handleChange}
                            fullWidth
                            onBlur={validateOnBlur}
                            value={manufacturerType}
                            error={
                                manufacturerTypeValidation && !manufacturerType
                            }
                            helperText={
                                manufacturerTypeValidation && !manufacturerType
                                    ? 'Please enter a Manufacturer Type.'
                                    : ''
                            }
                            disabled={permissionsCheck || superAdmin !== true}
                        />
                    </Grid>
                </FormSection>
            </Grid>
            <Grid item xs={12} md={12} sx={{ marginTop: -2, marginBottom: -2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormSection
                            size={12}
                            title="Manufacturer Logo Uploader"
                            reset={
                                <Stack direction="row" spacing={1}>
                                    {(logo || manufacturerLogo) && (
                                        <Tooltip title="Replace Current Logo">
                                            <IconButton
                                                component="label"
                                                disabled={permissionCheck}
                                            >
                                                <UploadFile />
                                                <VisuallyHiddenInput
                                                    id="logo-upload-input"
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) =>
                                                        handleFileSelect(
                                                            e,
                                                            true,
                                                            false
                                                        )
                                                    }
                                                    disabled={permissionCheck}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Reset to Original Logo">
                                        <IconButton
                                            onClick={handleLogoReset}
                                            disabled={permissionCheck}
                                        >
                                            <RestartAlt />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            }
                        >
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="center">
                                    {logo || manufacturerLogo ? (
                                        <Card
                                            sx={{
                                                width: { xs: '100%', sm: 320 },
                                                maxWidth: 400,
                                                position: 'relative',
                                                overflow: 'hidden',
                                                boxShadow: 3,
                                                borderRadius: 2,
                                                cursor: 'zoom-in',
                                                '&:hover .overlay': {
                                                    opacity: 1,
                                                },
                                            }}
                                            onClick={() =>
                                                setState({
                                                    openLogoPreview: true,
                                                })
                                            }
                                        >
                                            <CardMedia
                                                component="img"
                                                height="240"
                                                image={logo || manufacturerLogo}
                                                alt="Manufacturer Logo"
                                                sx={{
                                                    objectFit: 'contain',
                                                    bgcolor: 'background.paper',
                                                    p: 2,
                                                }}
                                            />
                                        </Card>
                                    ) : (
                                        <label htmlFor="logo-upload-input">
                                            <Tooltip title="Upload a Logo">
                                                <Card
                                                    component="div"
                                                    sx={{
                                                        width: 300,
                                                        height: 200,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        border: '2px dashed',
                                                        borderColor: 'grey.400',
                                                        borderRadius: 2,
                                                        bgcolor: 'black',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            bgcolor: 'tertiary',
                                                            borderColor:
                                                                'primary.main',
                                                        },
                                                    }}
                                                >
                                                    <Photo
                                                        sx={{
                                                            fontSize: 56,
                                                            color: 'grey.500',
                                                            mb: 1,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="subtitle1"
                                                        color="text.secondary"
                                                    >
                                                        Upload Logo
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        PNG or JPG
                                                    </Typography>
                                                </Card>
                                            </Tooltip>

                                            <VisuallyHiddenInput
                                                id="logo-upload-input"
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) =>
                                                    handleFileSelect(
                                                        e,
                                                        true,
                                                        false
                                                    )
                                                }
                                                disabled={permissionCheck}
                                            />
                                        </label>
                                    )}
                                </Box>

                                {(logo || manufacturerLogo) && (
                                    <Box mt={1} textAlign="center">
                                        <Chip
                                            label={
                                                logo
                                                    ? `New Logo Selected - ${logoFile?.name}`
                                                    : `Current Logo for ${manufacturerName}`
                                            }
                                            color={logo ? 'success' : 'primary'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                )}
                                <Modal
                                    open={openLogoPreview}
                                    onClose={() =>
                                        setState({ openLogoPreview: false })
                                    }
                                    closeAfterTransition
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Fade in={!!openLogoPreview}>
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                maxWidth: '90vw',
                                                maxHeight: '90vh',
                                                bgcolor: 'background.paper',
                                                boxShadow: 24,
                                                borderRadius: 2,
                                                p: 2,
                                                outline: 'none',
                                            }}
                                        >
                                            <IconButton
                                                onClick={() =>
                                                    setState({
                                                        openLogoPreview: false,
                                                    })
                                                }
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: 8,
                                                    color: 'grey.500',
                                                    bgcolor: 'background.paper',
                                                    '&:hover': {
                                                        bgcolor: 'grey.200',
                                                    },
                                                }}
                                            >
                                                <Close />
                                            </IconButton>

                                            <Box
                                                component="img"
                                                src={logo || manufacturerLogo}
                                                alt="Enlarged Manufacturer Logo"
                                                sx={{
                                                    maxWidth: '100%',
                                                    maxHeight: '80vh',
                                                    objectFit: 'contain',
                                                    borderRadius: 1,
                                                }}
                                            />
                                        </Box>
                                    </Fade>
                                </Modal>
                            </Grid>
                        </FormSection>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormSection
                            title="Manufacturer Login Video Uploader"
                            reset={
                                <Stack direction="row" spacing={1}>
                                    {(loginVideo || manufacturerVideo) && (
                                        <Tooltip title="Replace Current Login Video">
                                            <IconButton
                                                component="label"
                                                disabled={permissionCheck}
                                            >
                                                <UploadFile />
                                                <VisuallyHiddenInput
                                                    type="file"
                                                    accept="video/*"
                                                    onChange={(e) =>
                                                        handleFileSelect(
                                                            e,
                                                            false,
                                                            true
                                                        )
                                                    }
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    <Tooltip title="Reset to Original">
                                        <IconButton
                                            onClick={handleVideoReset}
                                            disabled={permissionCheck}
                                        >
                                            <RestartAlt />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            }
                        >
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="center">
                                    <label htmlFor="video-upload-input">
                                        {loginVideo || manufacturerVideo ? (
                                            <Card
                                                sx={{
                                                    width: {
                                                        xs: '100%',
                                                        sm: 500,
                                                    },
                                                    maxWidth: 600,
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    boxShadow: 3,
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    '&:hover .overlay': {
                                                        opacity: 1,
                                                    },
                                                }}
                                                component="button"
                                                type="button"
                                            >
                                                <CardMedia
                                                    component="video"
                                                    height="280"
                                                    controls
                                                    src={
                                                        loginVideo ||
                                                        manufacturerVideo
                                                    }
                                                    sx={{ background: '#000' }}
                                                />

                                                <Box
                                                    className="overlay"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        bgcolor: alpha(
                                                            '#000',
                                                            0.6
                                                        ),
                                                        color: 'white',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        opacity: 0,
                                                        transition:
                                                            'opacity 0.3s',
                                                        pointerEvents: 'none',
                                                    }}
                                                ></Box>

                                                <VisuallyHiddenInput
                                                    id="video-upload-input"
                                                    type="file"
                                                    accept="video/*"
                                                    onChange={(e) =>
                                                        handleFileSelect(
                                                            e,
                                                            false,
                                                            true
                                                        )
                                                    }
                                                    disabled={permissionCheck}
                                                />
                                            </Card>
                                        ) : (
                                            <Tooltip title="Upload Login Video">
                                                <Card
                                                    component="label"
                                                    sx={{
                                                        width: 300,
                                                        height: 200,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        border: '2px dashed',
                                                        borderColor: 'grey.400',
                                                        borderRadius: 2,
                                                        bgcolor: 'black',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            bgcolor: 'tertiary',
                                                            borderColor:
                                                                'primary.main',
                                                        },
                                                    }}
                                                >
                                                    <VideoLibrary
                                                        sx={{
                                                            fontSize: 60,
                                                            color: 'grey.500',
                                                            mb: 1,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="subtitle1"
                                                        color="text.secondary"
                                                    >
                                                        Upload Video
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        MP4 to 100MB
                                                    </Typography>

                                                    <VisuallyHiddenInput
                                                        id="video-upload-input"
                                                        type="file"
                                                        accept="video/*"
                                                        onChange={(e) =>
                                                            handleFileSelect(
                                                                e,
                                                                false,
                                                                true
                                                            )
                                                        }
                                                        disabled={
                                                            permissionCheck
                                                        }
                                                    />
                                                </Card>
                                            </Tooltip>
                                        )}
                                    </label>
                                </Box>

                                {(loginVideo || manufacturerVideo) && (
                                    <Box mt={1} textAlign="center">
                                        <Chip
                                            label={
                                                loginVideo
                                                    ? `New Video Selected - ${loginVideoFile?.name}`
                                                    : `Current Login Video for ${manufacturerName} `
                                            }
                                            color={
                                                loginVideo
                                                    ? 'success'
                                                    : 'primary'
                                            }
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                )}
                            </Grid>
                        </FormSection>
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                        <FormSection
                            title="Manufacturer Documents"
                            spacing={1}
                            reset={
                                <Tooltip title={'Reset Documents'}>
                                    <IconButton
                                        onClick={handleDocumentReset}
                                        disabled={permissionCheck}
                                    >
                                        <RestartAlt sx={{ fontSize: 30 }} />
                                    </IconButton>
                                </Tooltip>
                            }
                        >
                            <Grid item xs={12}>
                                <Accordion expanded={expandEula}>
                                    <AccordionSummary
                                        onClick={() =>
                                            setState({
                                                expandEula: !expandEula,
                                            })
                                        }
                                        expandIcon={
                                            <Tooltip
                                                title={'Show EULA Preview'}
                                            >
                                                <ExpandMore />
                                            </Tooltip>
                                        }
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Button
                                                    component="label"
                                                    title={
                                                        manufacturerEula?.length !==
                                                        0
                                                            ? 'Replace Current EULA'
                                                            : 'Upload EULA'
                                                    }
                                                >
                                                    <DriveFolderUpload
                                                        sx={{ fontSize: 40 }}
                                                    />

                                                    <Typography
                                                        variant="h5"
                                                        color={'primary'}
                                                        alignContent="center"
                                                        sx={{ marginLeft: 1 }}
                                                    >
                                                        {eula
                                                            ? eulaFile.name
                                                            : manufacturerEula
                                                            ? 'Replace Eula PDF'
                                                            : 'Upload EULA PDF'}
                                                        {eula ? <Check /> : ''}
                                                    </Typography>

                                                    <input
                                                        type="file"
                                                        accept=".pdf"
                                                        onChange={
                                                            handleEulaUpload
                                                        }
                                                        disabled={
                                                            permissionCheck
                                                        }
                                                        style={{
                                                            display: 'none',
                                                        }}
                                                    />
                                                </Button>
                                            </Grid>
                                            {manufacturerEula && (
                                                <Grid item xs={6}>
                                                    <Button
                                                        onClick={() =>
                                                            setState({
                                                                openEula: true,
                                                            })
                                                        }
                                                        size="large"
                                                        sx={{
                                                            display:
                                                                eula !== ''
                                                                    ? 'none'
                                                                    : null,
                                                        }}
                                                    >
                                                        <PictureAsPdf
                                                            sx={{
                                                                fontSize: 40,
                                                            }}
                                                            color="primary"
                                                        />
                                                        <Typography
                                                            variant="h5"
                                                            alignContent="right"
                                                            sx={{
                                                                marginLeft: 1,
                                                            }}
                                                        >
                                                            View Current Eula
                                                        </Typography>
                                                    </Button>
                                                    <UniversalModal
                                                        open={openEula}
                                                        onClose={() => {
                                                            setState({
                                                                openEula: false,
                                                            })
                                                        }}
                                                    >
                                                        <PDFViewer
                                                            url={
                                                                manufacturerEula
                                                            }
                                                        />
                                                    </UniversalModal>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid
                                            container
                                            sx={{ height: '100vh' }}
                                        >
                                            {eula ? (
                                                <iframe
                                                    title="EULA"
                                                    width="100%"
                                                    height="100%"
                                                    style={{
                                                        overflow: 'scroll',
                                                    }}
                                                    src={eula}
                                                />
                                            ) : (
                                                <Typography
                                                    color={'primary'}
                                                    variant="h5"
                                                >
                                                    No PDF Available For
                                                    Preview.
                                                </Typography>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>

                            <Grid item xs={12} sx={{ marginBottom: 9 }}>
                                <Accordion expanded={expandPrivacyPolicy}>
                                    <AccordionSummary
                                        onClick={() =>
                                            setState({
                                                expandPrivacyPolicy:
                                                    !expandPrivacyPolicy,
                                            })
                                        }
                                        expandIcon={
                                            <Tooltip
                                                title={
                                                    'Show Privacy Policy Preview'
                                                }
                                            >
                                                <ExpandMore />
                                            </Tooltip>
                                        }
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Button
                                                    component="label"
                                                    title={
                                                        manufacturerPrivacyPolicy?.length !==
                                                        0
                                                            ? 'Replace Current Privacy Policy'
                                                            : 'Upload Privacy Policy'
                                                    }
                                                >
                                                    <DriveFolderUpload
                                                        sx={{ fontSize: 40 }}
                                                    />

                                                    <Typography
                                                        variant="h5"
                                                        color={'primary'}
                                                        alignContent="center"
                                                        sx={{ marginLeft: 1 }}
                                                    >
                                                        {privacyPolicy
                                                            ? privacyFile.name
                                                            : manufacturerPrivacyPolicy
                                                            ? 'Replace Privacy Policy'
                                                            : 'Upload Privacy Policy'}
                                                        {privacyPolicy ? (
                                                            <Check />
                                                        ) : (
                                                            ''
                                                        )}
                                                    </Typography>

                                                    <input
                                                        type="file"
                                                        accept=".pdf"
                                                        onChange={
                                                            handlePrivacyUpload
                                                        }
                                                        disabled={
                                                            permissionCheck
                                                        }
                                                        style={{
                                                            display: 'none',
                                                        }}
                                                    />
                                                </Button>
                                            </Grid>
                                            {manufacturerPrivacyPolicy && (
                                                <Grid item xs={6}>
                                                    <Button
                                                        onClick={() =>
                                                            setState({
                                                                openPrivacyPolicy:
                                                                    true,
                                                            })
                                                        }
                                                        size="large"
                                                        sx={{
                                                            display:
                                                                privacyPolicy !==
                                                                ''
                                                                    ? 'none'
                                                                    : null,
                                                        }}
                                                    >
                                                        <PictureAsPdf
                                                            sx={{
                                                                fontSize: 40,
                                                            }}
                                                            color="primary"
                                                        />
                                                        <Typography
                                                            variant="h5"
                                                            alignContent="right"
                                                            sx={{
                                                                marginLeft: 1,
                                                            }}
                                                        >
                                                            View Current Privacy
                                                            Policy
                                                        </Typography>
                                                    </Button>
                                                    <UniversalModal
                                                        open={openPrivacyPolicy}
                                                        onClose={() => {
                                                            setState({
                                                                openPrivacyPolicy:
                                                                    false,
                                                            })
                                                        }}
                                                    >
                                                        <PDFViewer
                                                            url={
                                                                manufacturerPrivacyPolicy
                                                            }
                                                        />
                                                    </UniversalModal>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid
                                            container
                                            sx={{ height: '100vh' }}
                                        >
                                            {privacyPolicy ? (
                                                <iframe
                                                    title="PrivacyPolicy"
                                                    width="100%"
                                                    height="100%"
                                                    style={{
                                                        overflow: 'scroll',
                                                    }}
                                                    src={privacyPolicy}
                                                />
                                            ) : (
                                                <Typography
                                                    color={'primary'}
                                                    variant="h5"
                                                >
                                                    No PDF Available For
                                                    Preview.
                                                </Typography>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        </FormSection>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ marginBottom: -2 }}>
                        <FormSection
                            title="Manufacturer Color Palette"
                            spacing={1}
                            reset={
                                <Tooltip title={'Reset Color Palette'}>
                                    <IconButton
                                        onClick={handleColorReset}
                                        disabled={permissionCheck}
                                    >
                                        <RestartAlt sx={{ fontSize: 30 }} />
                                    </IconButton>
                                </Tooltip>
                            }
                        >
                            <Grid item xs={12} sx={{ padding: 2 }}>
                                <MuiColorInput
                                    value={manufacturerPrimaryColor}
                                    onChange={(value) =>
                                        handleColorChange(
                                            'manufacturerPrimaryColor',
                                            value
                                        )
                                    }
                                    label="Primary Color"
                                    name="manufacturerPrimaryColor"
                                    sx={{ width: '100%' }}
                                    disabled={permissionCheck}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ padding: 2 }}>
                                <MuiColorInput
                                    value={manufacturerSecondaryColor}
                                    onChange={(value) =>
                                        handleColorChange(
                                            'manufacturerSecondaryColor',
                                            value
                                        )
                                    }
                                    label="Secondary Color"
                                    name="manufacturerSecondaryColor"
                                    sx={{ width: '100%' }}
                                    disabled={permissionCheck}
                                />
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sx={{ padding: 2, marginBottom: 8 }}
                            >
                                <MuiColorInput
                                    value={manufacturerTertiaryColor}
                                    onChange={(value) =>
                                        handleColorChange(
                                            'manufacturerTertiaryColor',
                                            value
                                        )
                                    }
                                    label="Tertiary Color"
                                    name="manufacturerTertiaryColor"
                                    sx={{ width: '100%' }}
                                    disabled={permissionCheck}
                                />
                            </Grid>

                            <Dialog
                                open={openHelper}
                                onClose={() => setState({ openHelper: false })}
                                maxWidth="sm"
                                fullWidth
                            >
                                <Box>
                                    <DialogTitle align="center">
                                        File Upload
                                    </DialogTitle>
                                    <DialogContent>
                                        <Typography
                                            variant="body1"
                                            gutterBottom
                                            sx={{ marginTop: 2 }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <FiberManualRecord
                                                    sx={{ mr: 1 }}
                                                />
                                                <span>
                                                    A file marked for upload
                                                    cannot exceed{' '}
                                                    <strong>50 MB</strong>
                                                </span>
                                            </Box>
                                        </Typography>
                                    </DialogContent>
                                </Box>
                            </Dialog>
                        </FormSection>
                    </Grid>
                    {row?.id && superAdmin && (
                        <Grid
                            item
                            xs={12}
                            sx={{ marginBottom: -2, marginTop: -2 }}
                        >
                            <FormSection
                                title="Manufacturer Administrators"
                                titleAlignment="left"
                            >
                                <Grid item xs={12}>
                                    <ManufacturerAdmins
                                        manufacturerId={id}
                                        manufacturerName={manufacturerName}
                                        manufacturerAdmins={manufacturerAdmins}
                                        onAdminsChange={handleAdminsChange}
                                        permissionCheck={permissionCheck}
                                    />
                                </Grid>
                            </FormSection>
                        </Grid>
                    )}
                    {row?.id && superAdmin && (
                        <Grid
                            item
                            xs={12}
                            sx={{ marginBottom: -2, marginTop: 0 }}
                        >
                            <FormSection
                                title="Manufacturer Notification Administrators"
                                titleAlignment="left"
                            >
                                <Grid item xs={12}>
                                    <ManufacturerNotificationAdmins
                                        manufacturerName={manufacturerName}
                                        manufacturerAdmins={manufacturerAdmins}
                                        manufacturerNotificationAdmins={
                                            manufacturerNotificationAdmins
                                        }
                                        onNotificationAdminsChange={
                                            handleNotificationAdminsChange
                                        }
                                        permissionCheck={permissionCheck}
                                    />
                                </Grid>
                            </FormSection>
                        </Grid>
                    )}
                    <Grid item xs={12} sx={{ marginBottom: -1.5 }}>
                        <FormSection
                            reset={
                                <Button
                                    onClick={() =>
                                        setState({ openGuidelines: true })
                                    }
                                >
                                    <HelpOutline />
                                </Button>
                            }
                        >
                            <ManufacturerDTCUploader
                                manufacturerId={id}
                                permissionsCheck={permissionCheck}
                                makeFormDirty={() =>
                                    setState({
                                        formValidation: {
                                            ...formValidation,
                                            isFormDirty: true,
                                        },
                                    })
                                }
                                onUploadComplete={(data) => {
                                    setState({
                                        formData: {
                                            ...formData,
                                            dtcData:
                                                data?.parsedFormData
                                                    ?.stringifyDTCData,
                                        },
                                        formValidation: {
                                            ...formValidation,
                                            isFormDirty: true,
                                        },
                                    })
                                }}
                            />
                        </FormSection>

                        <Dialog
                            open={openGuidelines}
                            onClose={() => setState({ openGuidelines: false })}
                            maxWidth="sm"
                            fullWidth
                        >
                            <DialogTitle>
                                Instructions for Manufacturer DTC File Uploading
                            </DialogTitle>
                            <DialogContent>
                                <Typography variant="body1" gutterBottom>
                                    1. Accepted File Types:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ marginLeft: 2 }}
                                >
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li> CSV, XLSX, JSON.</li>
                                    </ul>
                                </Typography>
                                <br />
                                <Typography variant="body1" gutterBottom>
                                    2. Required Fields/Columns:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ marginLeft: 2 }}
                                >
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            SPN, FMI, Description, Severity,
                                            Device.
                                        </li>
                                    </ul>
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            Any other fields/columns except the
                                            above may cause errors or be
                                            obmited.
                                        </li>
                                    </ul>
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            The columns are case sensitive and
                                            should be submitted exactly as shown
                                            above.
                                        </li>
                                    </ul>
                                </Typography>
                                <br />
                                <Typography variant="body1" gutterBottom>
                                    3. File Selection:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ marginLeft: 2 }}
                                >
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            {' '}
                                            Click on the Select File button to
                                            find a document to upload.{' '}
                                        </li>{' '}
                                    </ul>
                                </Typography>
                                <br />
                                <Typography variant="body1" gutterBottom>
                                    4. Saving Changes:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{ marginLeft: 2 }}
                                >
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            Once the file is selected a preview
                                            will show demonstrating a file has
                                            been succesfully read.
                                        </li>
                                    </ul>
                                    <br />
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            Click the Upload button to verify
                                            the desired file to upload.
                                        </li>
                                    </ul>
                                    <br />
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            lastly click the button save and
                                            close found on the bottom of the
                                            form to properly submit any changes.
                                        </li>
                                    </ul>
                                    <br />
                                    <ul
                                        style={{
                                            margin: 0,
                                            paddingLeft: '1.5rem',
                                        }}
                                    >
                                        <li>
                                            The same logic is applied to edits
                                            made in the DTC Table. Once a cell
                                            has been edited:
                                            <Typography
                                                variant="body2"
                                                component="div"
                                                sx={{ marginLeft: 2 }}
                                            >
                                                1. Press enter or click off of
                                                the cell. <br />
                                                2. Click Save Table Changes
                                                found in the top right corner of
                                                the table.
                                                <br />
                                                3. Click the bottom save and
                                                close button to properly submit
                                                any changes
                                            </Typography>
                                        </li>
                                    </ul>

                                    <br />
                                </Typography>
                            </DialogContent>
                        </Dialog>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12} sx={{ marginBottom: -4 }}>
                <SaveButton
                    handleSubmit={handleSubmit}
                    handleReset={handleReset}
                    disabledSave={!isFormValid || permissionCheck}
                    disabledReset={!isFormDirty || permissionCheck}
                />
            </Grid>
        </Grid>
    )
}

export default ManufacturerForm
